import { Brain } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { AI_RANK_CONFIG } from '../../lib/aiRank';

/**
 * RankBadge — shared AI fit-rank display for the Sentinel feeds. Shows the
 * color tier plus (when available) the numeric score and the AI's
 * reasoning, rather than just a colored label with a hover-only tooltip.
 *
 * variant="compact" — table-cell badge (default).
 * variant="detail"  — badge + reasoning paragraph for detail/side panes.
 */
export default function RankBadge({ rank, score, reasoning, basis, variant = 'compact' }) {
  if (!rank) return null;

  const config = AI_RANK_CONFIG[rank];
  // A score derived from a title alone is far weaker than one derived from a
  // PWS, and the badge looked identical either way. Say so in the tooltip and
  // mark it, so a thin score isn't mistaken for a grounded one.
  const thin = basis === 'title_and_description';
  const tooltip =
    `AI Fit: ${rank}${score != null ? ` (${score}%)` : ''} — ${config?.tip}` +
    (thin ? '\n\n⚠ Scored on title and description only — no requirements document was available.' : '') +
    (reasoning ? '\n' + reasoning : '');

  if (variant === 'detail') {
    return (
      <>
        <Badge
          className={cn('text-[10px] font-semibold', config?.cls)}
          title={tooltip}
        >
          <Brain className="w-2.5 h-2.5 mr-0.5" />
          {config?.label}
          {score != null && ` · ${score}%`}
          {thin && <span className="ml-1 opacity-80" aria-label="scored without a requirements document">*</span>}
        </Badge>
        {reasoning && (
          <p className="text-purple-700 text-xs flex items-start gap-1.5">
            <Brain className="w-3 h-3 mt-0.5 flex-shrink-0 text-purple-500" />
            {reasoning}
          </p>
        )}
      </>
    );
  }

  return (
    <Badge
      className={cn('text-[10px] font-semibold w-fit cursor-default', config?.cls)}
      title={tooltip}
    >
      {config?.label}
    </Badge>
  );
}
